import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { History as HistoryIcon, Undo2, Redo2, Bot } from 'lucide-react';
import { listHistory, undo, redo, undoWithBlockers, type HistoryEntry, type HistoryOutcome } from '../lib/history';
import { getEntityUrl } from '../lib/entityUrls';
import { formatRelativeTime, useNow } from '../lib/relativeTime';
import { useToast } from '../contexts/ToastContext';

/**
 * Action history (#278): everything you did, newest first, each undoable on
 * its own - not only the latest. Typing bursts are not listed (Ctrl+Z still
 * undoes them). A refused undo explains why and offers to undo the actions in
 * the way first.
 */

interface Refusal { id: string; outcome: Extract<HistoryOutcome, { ok: false }> }

/** Where a history row links: only to entities with a detail page of their own. */
function entityLink(e: HistoryEntry): string | null {
  if (!e.entity_type || !e.entity_id || e.undone_at) return null;
  if (e.entity_type === 'meeting_instance' || e.entity_type === 'tag' || e.entity_type === 'quick_capture') return null;
  const url = getEntityUrl({ entity_type: e.entity_type, entity_id: e.entity_id, name: e.label });
  return url === '/' ? null : url;
}

export function HistoryPage() {
  useNow();
  const toast = useToast();
  const [entries, setEntries] = useState<HistoryEntry[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<Refusal | null>(null);

  const load = useCallback(async () => {
    try {
      setEntries(await listHistory());
    } catch {
      toast.error('Could not load history');
      setEntries([]);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  async function run(id: string, op: () => Promise<HistoryOutcome>, done: string) {
    setBusy(id);
    setRefusal(null);
    try {
      const r = await op();
      if (r.ok) toast.success(`${done}: ${r.label}`);
      else setRefusal({ id, outcome: r });
    } finally {
      setBusy(null);
      // The page remounts on history changes; reload here for refusals too.
      void load();
    }
  }

  return (
    <div className="h-full overflow-auto p-3 sm:p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold text-text-primary mb-2">History</h1>
        <p className="text-sm text-text-muted mb-6">
          Undo anything you did in the last 7 days. Press Ctrl+Z outside a text field to undo your latest action.
        </p>

        {entries === null ? (
          <p className="text-sm text-text-muted">Loading...</p>
        ) : entries.length === 0 ? (
          <div className="text-center py-16 text-text-muted">
            <HistoryIcon size={32} className="mx-auto mb-3 opacity-50" />
            <p className="text-sm">Nothing yet. Actions you take will appear here.</p>
          </div>
        ) : (
          <ul className="space-y-1" aria-label="Action history">
            {entries.map((e) => {
              const link = entityLink(e);
              const undone = !!e.undone_at;
              return (
                <li key={e.id} className="rounded-lg hover:bg-bg-hover transition-colors">
                  <div className="flex items-center gap-3 px-3 py-2.5">
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm truncate ${undone ? 'text-text-muted line-through' : 'text-text-primary'}`}>
                        {link ? <Link to={link} className="hover:underline">{e.label}</Link> : e.label}
                      </p>
                      <p className="text-xs text-text-muted flex items-center gap-1.5">
                        <span>{formatRelativeTime(e.updated_at)}</span>
                        {(e.source === 'mcp' || e.source === 'api_key') && (
                          <span className="inline-flex items-center gap-1"><Bot size={12} /> via Claude</span>
                        )}
                        {undone && <span>- undone</span>}
                      </p>
                    </div>
                    <button
                      type="button"
                      disabled={busy !== null}
                      onClick={() => void run(e.id, () => (undone ? redo(e.id) : undo(e.id)), undone ? 'Redid' : 'Undid')}
                      className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md border border-border text-text-secondary hover:text-text-primary hover:border-border-hover disabled:opacity-50"
                      aria-label={`${undone ? 'Redo' : 'Undo'} ${e.label}`}
                    >
                      {undone ? <Redo2 size={14} /> : <Undo2 size={14} />}
                      {undone ? 'Redo' : 'Undo'}
                    </button>
                  </div>
                  {refusal?.id === e.id && (
                    <div role="alert" className="mx-3 mb-2 px-3 py-2 rounded-md bg-danger/10 text-sm text-text-secondary">
                      <p>{refusal.outcome.error}</p>
                      {!undone && refusal.outcome.blockers.some((b) => b.id) && (
                        <button
                          type="button"
                          disabled={busy !== null}
                          className="mt-1.5 text-xs text-accent hover:underline disabled:opacity-50"
                          onClick={() => void run(e.id, () => undoWithBlockers(e.id, refusal.outcome.blockers), 'Undid')}
                        >
                          Undo those first
                        </button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
